import styles from './TransactionSessionDetail.module.css'
import { IFText, CostBreakdown } from 'Components'
import { Colors } from 'Theme'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import PropTypes from 'prop-types'
import { formatDate } from '../../Utils/StringFunctions'
import { hasBalance, formatBalanceAmount } from '../../Utils/BalanceFunctions'

const TransactionSessionDetail = ({ row, pointsTariff, timelineSlot }) => {
  const getTimeBetweenDates = (firstDate, secondDate) => {
    var firstMomentDate = moment(firstDate)
    var secondMomentDate = moment(secondDate)
    var chargingTimeInMinutes = secondMomentDate.diff(firstMomentDate, 'm')
    return (
      Math.floor(chargingTimeInMinutes / 60) +
      'h' +
      ' ' +
      (chargingTimeInMinutes % 60) +
      'm'
    )
  }
  const { t } = useTranslation()
  const duration = getTimeBetweenDates(
    row.original.startedAt,
    row.original.details.endedAt,
  )
  const amountExists =
    row.original.amount === 0 || row.original.amount === '0' ? false : true
  const pointsExists =
    row.original.pointsAmount === 0 ||
    row.original.pointsAmount === undefined ||
    row.original.pointsAmount === '0'
      ? false
      : true
  const ChargingSessionDetailItems = [
    {
      key: 'Name',
      value: row.original.details?.cdrLocation?.name,
      translationKey: 'TransactionDetails.Name',
    },
    {
      key: 'Start',
      value: formatDate(row.original.startedAt),
      translationKey: 'TransactionDetails.Start',
    },
    {
      key: 'End',
      value: formatDate(row.original.details.endedAt),
      translationKey: 'TransactionDetails.End',
    },
    {
      key: 'Duration',
      value: duration,
      translationKey: 'TransactionDetails.Duration',
    },
    {
      key: 'Consumption',
      value: row.original.energyConsumption ?? '-',
      translationKey: 'TransactionDetails.Consumption',
    },
    {
      key: 'Tariff',
      value: row.original.tariff || '-',
      translationKey: 'TransactionDetails.Tariff',
    },
    {
      key: 'PointsTariff',
      value: pointsExists ? pointsTariff?.toFixed(3) : '-',
      translationKey: 'TransactionDetails.PointsTariff',
    },
    {
      key: 'ChargerType',
      value:
        row.original.details?.cdrLocation?.connectorPowerType === 'DC'
          ? 'DC'
          : 'AC',
      translationKey: 'TransactionDetails.ChargerType',
    },
    {
      key: 'Remark',
      value: row.original.details?.remark || '-',
      translationKey: 'TransactionDetails.Remark',
    },
    {
      key: 'ConnectorUid',
      value: row.original.connectorUid,
      translationKey: 'TransactionDetails.ConnectorUid',
    },
    {
      key: 'EvseID',
      value: row.original?.evseUid ?? '-',
      translationKey: 'TransactionDetails.EvseID',
    },
  ]
  const TransactionDetailItems = [
    {
      key: 'TokenType',
      value: row.original.details?.cdrToken?.type ?? '-',
      translationKey: 'TransactionDetails.TokenType',
    },
    {
      key: 'TokenID',
      value:
        row.original?.startingChargingToken ||
        row.original?.details?.cdrToken?.uid ||
        '-',
      translationKey: 'TransactionDetails.TokenID',
    },
    {
      key: 'TransactionType',
      value: row.original.type,
      translationKey: 'TransactionDetails.TransactionType',
    },
    {
      key: 'Amount',
      value: amountExists
        ? row.original.isCredit
          ? `+${row.original.country?.currency?.code} ${Number(
              row.original.amount,
            ).toFixed(row.original.country?.currency?.decimalPlaces || 2)}`
          : `-${row.original.country?.currency?.code} ${Number(
              row.original.amount,
            ).toFixed(row.original.country?.currency?.decimalPlaces || 2)}`
        : '0',
      translationKey: 'TransactionDetails.Amount',
    },
    {
      key: 'PointsAmount',
      value: pointsExists
        ? row.original.isCredit
          ? `+PTS ${Intl.NumberFormat('en-US').format(
              row.original.pointsAmount,
            )}`
          : `-PTS ${Intl.NumberFormat('en-US').format(
              row.original.pointsAmount,
            )}`
        : '-',
      translationKey: 'TransactionDetails.PointsAmount',
    },
    {
      key: 'BalanceBefore',
      value: hasBalance(row.original)
        ? formatBalanceAmount(
            row.original.billingAccountBalance.before,
            row.original.country?.currency,
          )
        : t('TransactionDetails.BalanceNotRecorded'),
      translationKey: 'TransactionDetails.BalanceBefore',
    },
    {
      key: 'BalanceAfter',
      value: hasBalance(row.original)
        ? formatBalanceAmount(
            row.original.billingAccountBalance.after,
            row.original.country?.currency,
          )
        : t('TransactionDetails.BalanceNotRecorded'),
      translationKey: 'TransactionDetails.BalanceAfter',
    },
  ]

  return (
    <div
      className={styles.TransactionDetails}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.Line}>
        <div
          style={{ borderLeft: `1px solid ${Colors.DividerColor}` }}
          className={styles.Divider}
        />
        <div
          style={{ background: Colors.DividerColor }}
          className={styles.Circle}
        />
      </div>
      <div className={styles.DetailsContainer}>
        <div className={styles.Details}>
          <div className={styles.ChargingSessionDetails}>
            <IFText className={styles.TransactionTitle}>
              {t('TransactionDetails.CharginSessionTitle')}
            </IFText>
            <div className={styles.AllDetails}>
              {ChargingSessionDetailItems.map((item) => (
                <div className={styles.Detail} key={item.key}>
                  <div className={styles.LabelTitle}>
                    <IFText
                      className={styles.DetailText}
                      style={{ color: Colors.DetailPanel }}
                    >
                      {t(item.translationKey)}
                    </IFText>
                  </div>
                  <div className={styles.LabelValue}>
                    <IFText className={styles.DetailText}>{item.value}</IFText>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.ChargingSessionDetails}>
            <IFText className={styles.TransactionTitle}>
              {t('TransactionDetails.TransactionDetailsTitle')}
            </IFText>
            <div className={styles.AllDetails}>
              {TransactionDetailItems.map((item) => (
                <div className={styles.Detail} key={item.key}>
                  <div className={styles.LabelTitle}>
                    <IFText
                      className={styles.DetailText}
                      style={{ color: Colors.DetailPanel }}
                    >
                      {t(item.translationKey)}
                    </IFText>
                  </div>
                  <div className={styles.LabelValue}>
                    <IFText className={styles.DetailText}>{item.value}</IFText>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.costBreakdownContainer}>
          <CostBreakdown
            costBreakdown={row.original.costBreakdown}
            currency={row.original.country?.currency}
          />
          {timelineSlot}
        </div>
      </div>
    </div>
  )
}
TransactionSessionDetail.propTypes = {
  row: PropTypes.object,
}

export default TransactionSessionDetail
